import { ILeanProductTile } from "@faire/web-api/indigofair/data/ILeanProductTile";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import React from "react";
import styled, { css } from "styled-components";

import { LazyEncapsulatedQuickAdd } from "@faire/retailer/components/QuickAdd/LazyEncapsulatedQuickAdd";
import { RoundNotifyMeButton } from "@faire/retailer/components/RoundNotifyMeButton";
import { IRoundQuantityPickerProps } from "@faire/retailer/components/RoundQuantityPicker";
import { IRoundQuantityPickerStateProps } from "@faire/retailer/components/RoundQuantityPicker/UseRoundQuantityPickerState";
import { canAddToCart } from "@faire/retailer/lib/canAddToCart";
import { getIsBrandPreview } from "@faire/retailer/lib/getIsBrandPreview";
import { isLoggedInRetailer } from "@faire/retailer/lib/isLoggedInRetailer";
import { UpdateCartHandler } from "@faire/retailer/stores/domain/CartStore/consts";
import { useFaireProtection } from "@faire/retailer/stores/domain/FaireProtection/useFaireProtection";

import { ProductTileInfo } from "../../ProductTileInfo";
import { NotifyMeWrapper } from "../../styled";
import { PRODUCT_TILE_SURFACE } from "../TileImageOverlayContent";
import { useIsNonZeroQuantityPickerValue } from "../useIsNonZeroQuantityPickerValue";

interface IProps {
  productTile: ProductTileInfo;
  brand?: ILeanProductTile.IBrand;
  disableEventBadge?: boolean;
  updateCartFn?: UpdateCartHandler;
  imageContainerRef?: React.RefObject<HTMLDivElement>;
  renderCollapsableQuickAddButtonProps?: IRoundQuantityPickerProps &
    IRoundQuantityPickerStateProps;
  isSubscriedToBackInStock?: boolean;
  deleteBackInStockNotification?: (productOptionToken: string) => void;
  onQuickAdd?: (newQuantity?: number) => void;
  onQuickAddSubmit?: (newQuantity?: number) => void;
  onChooseOption?: () => void;
  onChooseOptionQuantityUpdate?: (newQuantity?: number) => void;
  productTileSurface?: PRODUCT_TILE_SURFACE;
}

export const ProductTileQuickAdd: React.FC<IProps> = ({
  productTile,
  updateCartFn,
  imageContainerRef,
  renderCollapsableQuickAddButtonProps,
  isSubscriedToBackInStock,
  deleteBackInStockNotification,
  onQuickAdd,
  onQuickAddSubmit,
  onChooseOption,
  onChooseOptionQuantityUpdate,
  productTileSurface,
}) => {
  const availableUnits =
    productTile?.quick_add?.quick_add_option?.option_available_units ??
    Number.POSITIVE_INFINITY;

  const isSalesPaused =
    productTile?.product?.state === IProduct.State.SALES_PAUSED;

  const canAdd = canAddToCart();

  const canQuickAddProduct =
    canAdd && !getIsBrandPreview() && !isSalesPaused && availableUnits !== 0;

  const quickAddProductOptionToken =
    productTile?.quick_add?.quick_add_option?.option_token;

  const { onQuantityUpdate } = useIsNonZeroQuantityPickerValue(
    productTile?.product?.token ?? ""
  );

  const onQuickAddClick = (value?: number) => {
    onQuickAdd?.(value);
    onQuantityUpdate(value);
  };

  const shouldShowQuickAddOnLoad =
    productTileSurface === PRODUCT_TILE_SURFACE.EVALUATE;

  return (
    <RoundButton
      canQuickAddProduct={canQuickAddProduct}
      productTile={productTile}
      imageContainerRef={imageContainerRef}
      updateCartFn={updateCartFn}
      isSalesPaused={isSalesPaused}
      quickAddProductOptionToken={quickAddProductOptionToken}
      isSubscriedToBackInStock={isSubscriedToBackInStock}
      renderCollapsableQuickAddButtonProps={
        renderCollapsableQuickAddButtonProps
      }
      deleteBackInStockNotification={deleteBackInStockNotification}
      onQuickAdd={onQuickAddClick}
      onQuickAddSubmit={onQuickAddSubmit}
      onChooseOption={onChooseOption}
      onChooseOptionQuantityUpdate={onChooseOptionQuantityUpdate}
      shouldShowQuickAdd={shouldShowQuickAddOnLoad}
    />
  );
};

interface IRoundButtonProps {
  canQuickAddProduct: boolean;
  productTile: ProductTileInfo;
  imageContainerRef?: React.RefObject<HTMLDivElement>;
  updateCartFn?: UpdateCartHandler;
  isSalesPaused: boolean;
  quickAddProductOptionToken?: string;
  isSubscriedToBackInStock?: boolean;
  renderCollapsableQuickAddButtonProps?: IRoundQuantityPickerProps &
    IRoundQuantityPickerStateProps;
  deleteBackInStockNotification?: (productOptionToken: string) => void;
  onQuickAdd?: (newQuantity?: number) => void;
  onQuickAddSubmit?: (newQuantity?: number) => void;
  onChooseOption?: () => void;
  onChooseOptionQuantityUpdate?: (newQuantity?: number) => void;
  shouldShowQuickAdd?: boolean;
}

const RoundButton: React.FC<IRoundButtonProps> = ({
  canQuickAddProduct,
  productTile,
  imageContainerRef,
  updateCartFn,
  isSalesPaused,
  quickAddProductOptionToken,
  isSubscriedToBackInStock,
  renderCollapsableQuickAddButtonProps,
  deleteBackInStockNotification,
  onQuickAdd,
  onQuickAddSubmit,
  onChooseOption,
  onChooseOptionQuantityUpdate,
  shouldShowQuickAdd,
}) => {
  const { isBrandTokenForbidden } = useFaireProtection();
  const isFaireProtectionRestricted = isBrandTokenForbidden(
    productTile.product?.brand_token ?? ""
  );

  if (canQuickAddProduct && productTile?.quick_add) {
    return (
      <React.Suspense
        fallback={null}
        key={`${productTile.product?.token}-suspense`}
      >
        <StyledLazyEncapsulatedQuickAdd
          key={`${productTile.product?.token}-encapsulated-quick-add`}
          productToken={productTile.product?.token}
          quickAdd={productTile.quick_add}
          brandToken={productTile.product?.brand_token}
          imageContainerRef={imageContainerRef}
          updateItems={updateCartFn}
          renderCollapsableQuickAddButtonProps={
            renderCollapsableQuickAddButtonProps
          }
          showModalOnClick
          onQuickAddClick={onQuickAdd}
          onQuickAddSubmit={onQuickAddSubmit}
          onChooseOption={onChooseOption}
          onChooseOptionQuantityUpdate={onChooseOptionQuantityUpdate}
          disableQuickAddWithFaireProtectionTooltip={
            isFaireProtectionRestricted
          }
          $shouldShowQuickAdd={shouldShowQuickAdd}
        />
      </React.Suspense>
    );
  } else if (
    isSalesPaused &&
    quickAddProductOptionToken !== undefined &&
    isLoggedInRetailer()
  ) {
    return (
      <StyledNotifyMeWrapper
        onClick={(e) => e.preventDefault()}
        key={`${productTile.product?.token}-round-notify-me-picker`}
      >
        <RoundNotifyMeButton
          productOptionToken={quickAddProductOptionToken}
          isSubscriedToBackInStock={isSubscriedToBackInStock}
          deleteBackInStockNotification={deleteBackInStockNotification}
        />
      </StyledNotifyMeWrapper>
    );
  } else {
    return null;
  }
};

const StyledNotifyMeWrapper = styled(NotifyMeWrapper)`
  bottom: 8px;
  right: 8px;
`;

const StyledLazyEncapsulatedQuickAdd = styled(LazyEncapsulatedQuickAdd)<{
  $shouldShowQuickAdd?: boolean;
}>`
  ${(props) =>
    !props.$shouldShowQuickAdd
      ? css`
          transition: opacity 100ms ease-in-out;
          opacity: 0;
        `
      : ""}
`;
